import { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getUsers, createUser, updateUser, deleteUser } from '../services/api';

export const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
  });
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      setUsers(response.data || []);
    } catch (error) {
      toast.error(t('failedToLoadUsers'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '', // Don't pre-fill password for security
        full_name: user.full_name,
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'user',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'user',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // When editing, only send password if it was changed
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await updateUser(editingUser.id, updateData);
        toast.success(t('userUpdatedSuccessfully'));
      } else {
        await createUser(formData);
        toast.success(t('userCreatedSuccessfully'));
      }
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to save user';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t('confirmDeleteUser'))) {
      return;
    }

    try {
      await deleteUser(id);
      toast.success(t('userDeletedSuccessfully'));
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to delete user';
      toast.error(errorMsg);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('userManagement')}</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {t('manageSystemUsers')}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
          <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 4v16m8-8H4" />
          </svg>
          {t('addUser')}
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {users.map((user) => (
          <div key={user.id} className="bg-white shadow-md rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{user.full_name}</h3>
                <p className="text-sm text-gray-600 mt-1">{user.email}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {user.role}
              </span>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-xs text-gray-500">
                {t('created')}: {new Date(user.created_at).toLocaleDateString()}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOpenModal(user)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('edit') || 'Edit'}
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(user.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('delete') || 'Delete'}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('name')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('email')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('role')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('created')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className="text-sm font-medium text-gray-900">
                    {user.full_name}
                  </div>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className="text-sm text-gray-600">
                    {user.email}
                  </div>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                    <button
                      onClick={() => handleOpenModal(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title={t('edit') || 'Edit'}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900"
                      title={t('delete') || 'Delete'}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingUser ? t('editUser') : t('createUser')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('fullName')}
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder={t('placeholderFullName') || 'John Doe'}
            required
          />

          <Input
            label={t('email')}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('placeholderEmail') || 'john@example.com'}
            required
          />

          <Input
            label={editingUser ? t('passwordLabelOptional') : t('passwordLabel')}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={t('placeholderPassword') || '••••••••'}
            required={!editingUser}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('role')}
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="user">{t('user')}</option>
              <option value="admin">{t('admin')}</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingUser ? t('updateUser') : t('createUser')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal} className="flex-1">
              {t('cancel')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
